// Tier-based export-resolution clamp. Free/Starter cap at 1080p, Creator at 4K,
// Business at 8K (PRD §1). Bridges domain gating (maxResolution) to the
// monolith's export path: given the canvas size and the user's tier, returns the
// dimensions to export at, scaled down (aspect-ratio preserving) when the longest
// edge exceeds the tier cap. Pure, unit-tested.

import type { Resolution, Tier } from '../domain/types';
import { maxResolution } from '../domain/gating';

/** Longest-edge pixel cap per resolution tier. */
export const RESOLUTION_LONGEST_EDGE: Record<Resolution, number> = {
  '1080p': 1920,
  hd: 2560,
  '4k': 3840,
  '8k': 7680,
};

export interface ClampedExport {
  width: number;
  height: number;
  /** True when the source was larger than the tier cap and got scaled down. */
  scaled: boolean;
  /** The cap that applied (longest edge px). */
  capPx: number;
}

/** Longest-edge px the tier is allowed to export. */
export function maxExportLongestEdge(tier: Tier): number {
  return RESOLUTION_LONGEST_EDGE[maxResolution(tier)];
}

/**
 * Clamp an export size to the tier's max resolution, preserving aspect ratio.
 * Never upscales. Rounds to whole pixels and guarantees >= 1px per side.
 */
export function clampExportSize(width: number, height: number, tier: Tier): ClampedExport {
  const capPx = maxExportLongestEdge(tier);
  const longest = Math.max(width, height);
  if (longest <= capPx || longest === 0) {
    return { width, height, scaled: false, capPx };
  }
  const factor = capPx / longest;
  return {
    width: Math.max(1, Math.round(width * factor)),
    height: Math.max(1, Math.round(height * factor)),
    scaled: true,
    capPx,
  };
}
